import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n';
import { showModal, hideModal } from 'actions/modal';
import { navigateToAuthPage } from 'actions/app';
import { logoutUser } from 'actions/profile';
import { MODAL_TYPES } from 'constants/modal-types';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import Platform from 'platform';
import { getIsLifecellFlag } from 'reducers/profile/selectors';
import { selectIsPurchaseProtected } from 'reducers/parental-lock/selectors';

const useModals = () => {
  const dispatch = useDispatch();
  const isLifecellUser = useSelector(getIsLifecellFlag);
  const isPurchaseProtected = useSelector(selectIsPurchaseProtected);

  const onModalClose = useCallback(
    ({ sectionToFocus, elementToFocus }) => {
      dispatch(hideModal());
      SpatialNavigationWrapper.focus(sectionToFocus);

      if (elementToFocus) SpatialNavigationWrapper.focus(elementToFocus);
    },
    [dispatch],
  );

  const showLoginModal = useCallback(
    (focusBackConfig, search, title) => {
      const actionData = showModal(MODAL_TYPES.CONFIRM, {
        message: title || i18n.t('common.modal.loginModalTitle'),
        confirmText: i18n.t('common.modal.loginModalConfirm'),
        rejectText: i18n.t('common.modal.loginModalCancel'),
        onModalConfirm: () => {
          dispatch(hideModal());
          dispatch(navigateToAuthPage(search));
        },
        onModalBack: () => onModalClose(focusBackConfig),
      });
      dispatch(actionData);
    },
    [dispatch, onModalClose],
  );

  /**
   *
   * @type {(function(focusBackConfig, onSuccess, text?): void)|*}
   */

  const showEnterPinModal = useCallback(
    (focusBackConfig, onSuccess, text) => {
      const actionData = showModal(MODAL_TYPES.ENTER_PIN, {
        onModalBack: () => onModalClose(focusBackConfig),
        modalTitleText: text || i18n.t('pc.videoPinMessage'),
        onModalConfirm: () => {
          if (typeof onSuccess === 'function') onSuccess();
          dispatch(hideModal());
        },
        checkOnComplete: true,
      });
      dispatch(actionData);
    },
    [dispatch, onModalClose],
  );

  const showCreatePinModal = useCallback(
    (focusBackConfig, onSuccess) => {
      const actionData = showModal(MODAL_TYPES.ENTER_PIN, {
        onModalBack: () => onModalClose(focusBackConfig),
        modalTitleText: i18n.t('pc.createPin'),
        onModalConfirm: () => {
          if (typeof onSuccess === 'function') onSuccess();
          dispatch(hideModal());
        },
        type: 'createPin',
        checkOnComplete: true,
      });
      dispatch(actionData);
    },
    [dispatch, onModalClose],
  );

  const showExitModal = useCallback(
    (focusBackConfig) => {
      dispatch(
        showModal(MODAL_TYPES.CONFIRM, {
          message: i18n.t('common.exitAppMessage'),
          confirmText: i18n.t('common.ExitApp'),
          rejectText: i18n.t('common.cancel'),
          type: 'exit',
          onModalBack: () => {
            innerAnalyticsVolia.postEvent({
              eventGroup: 'notification',
              eventName: `confirm app close`,
              eventValue: `cancel`,
            });
            // order is important
            SpatialNavigationWrapper.focus(focusBackConfig.elementToFocus);
            dispatch(hideModal());
          },
          onModalConfirm: () => {
            innerAnalyticsVolia.postEvent({
              eventGroup: 'notification',
              eventName: `confirm app close`,
              eventValue: `confirm`,
            });
            Platform.exit();
            // need to focus back if it was performed in browser
            SpatialNavigationWrapper.focus(focusBackConfig.elementToFocus);
            dispatch(hideModal());
          },
        }),
      );
    },
    [dispatch],
  );

  const showLogoutModal = useCallback(
    (focusBackConfig) => {
      const onModalConfirm = () => {
        innerAnalyticsVolia.postEvent({
          eventGroup: 'notification',
          eventName: `confirm sign out`,
          eventValue: `show notification`,
        });

        dispatch(
          showModal(MODAL_TYPES.CONFIRM, {
            message: i18n.t('profile.logoutMessage'),
            confirmText: i18n.t('profile.logout'),
            rejectText: i18n.t('profile.cancel'),
            onModalConfirm: () => {
              dispatch(logoutUser());
              innerAnalyticsVolia.postEvent({
                eventGroup: 'notification',
                eventName: `confirm sign out`,
                eventValue: `confirm`,
              });
              onModalClose(focusBackConfig);
            },
            onModalBack: () => {
              innerAnalyticsVolia.postEvent({
                eventGroup: 'notification',
                eventName: `confirm sign out`,
                eventValue: `cancel`,
              });
              onModalClose(focusBackConfig);
            },
          }),
        );
      };

      dispatch(
        showModal(MODAL_TYPES.CONFIRM, {
          message: i18n.t('profile.account'),
          confirmText: isLifecellUser ? i18n.t('profile.logoutLifecell') : i18n.t('profile.logoutAccount'),
          rejectText: i18n.t('profile.back'),
          type: 'exit',
          onModalConfirm,
          onModalBack: () => {
            innerAnalyticsVolia.postEvent({
              eventGroup: 'notification',
              eventName: `confirm sign out`,
              eventValue: `cancel`,
            });
            onModalClose(focusBackConfig);
          },
        }),
      );
    },
    [dispatch, isLifecellUser, onModalClose],
  );

  const showPackageModal = useCallback(
    (pkg, focusBackConfig) => {
      dispatch(
        showModal(MODAL_TYPES.PACKAGE, {
          onModalBack: () => onModalClose(focusBackConfig),
          selectedPackage: pkg,
        }),
      );
    },
    [dispatch, onModalClose],
  );

  const showPortmonePaymentModal = useCallback(
    (params, focusBackConfig = {}) => {
      dispatch(
        showModal(MODAL_TYPES.PORTMONE, {
          onModalBack: () => {
            if (typeof params.onCancel === 'function') params.onCancel();
            onModalClose(focusBackConfig);
          },
          onModalConfirm: () => {
            if (typeof params.onSuccess === 'function') params.onSuccess();
            dispatch(hideModal());
          },
          ...params,
        }),
      );
    },
    [dispatch, onModalClose],
  );

  const showLifecellPaymentModal = useCallback(
    (params, focusBackConfig) => {
      const modalAction = () => {
        dispatch(
          showModal(MODAL_TYPES.LIFECELL_PAYMENT, {
            onModalBack: () => {
              if (typeof params?.onCancel === 'function') params.onCancel();
              onModalClose(focusBackConfig);
            },
            onModalConfirm: () => {
              if (typeof params?.onSuccess === 'function') params.onSuccess();
              dispatch(hideModal());
            },
            ...params,
          }),
        );
      };

      if (isPurchaseProtected) {
        // showEnterPinModal calls "hideModal" under the hood, so this little timeout make it has no effect on Lifecell modal
        showEnterPinModal(focusBackConfig, () => setTimeout(modalAction, 0), i18n.t('pc.pinConfirmation'));
      } else {
        modalAction();
      }
    },
    [dispatch, isPurchaseProtected, onModalClose, showEnterPinModal],
  );

  return {
    showLoginModal,
    showEnterPinModal,
    showCreatePinModal,
    showPackageModal,
    showExitModal,
    showLogoutModal,
    showPortmonePaymentModal,
    showLifecellPaymentModal,
  };
};

export default useModals;
